import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, ChevronsUpDown, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { useDebounce } from '@/lib/hooks';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@/components/ui/popover';
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '@/components/ui/command';
import { api } from '@/lib/api/client';

// eslint_disable-next-line @typescript-eslint/no-explicit-any
type AnyRecord = Record<string, any>;

const DEFAULT_PARAMS: Record<string, unknown> = {};
const DEFAULT_SELECTED_IDS: (number | string)[] = [];
const DEFAULT_HIDDEN_IDS: (number | string)[] = [];

export interface UniversalMultiComboboxProps {
  url: string;
  listUrl?: string;
  labelKey?: string;
  valueKey?: string;
  onSelectItem?: (items: AnyRecord[]) => void;
  placeholder?: string;
  queryKey?: string;
  params?: Record<string, unknown>;
  className?: string;
  selectedIds?: (number | string)[];
  hiddenIds?: (number | string)[];
  disabled?: boolean;
  /**
   * When true, renders "Select all" / "Deselect all" controls that select every
   * record matching `params` (the whole pool, ignoring the search box and
   * excluding `hiddenIds`). Useful for company-wide selections.
   */
  enableSelectAll?: boolean;
}

interface FiltersState {
  limit: number;
  page: number;
  s: string | undefined;
  [key: string]: unknown;
}

interface ApiResponse {
  results?: AnyRecord[];
  data?: AnyRecord[];
}

export function UniversalMultiCombobox({
  url,
  listUrl,
  labelKey = 'name',
  valueKey = 'id',
  onSelectItem,
  placeholder = 'Select...',
  queryKey = 'multiComboData',
  params = DEFAULT_PARAMS,
  className = '',
  selectedIds = DEFAULT_SELECTED_IDS,
  hiddenIds = DEFAULT_HIDDEN_IDS,
  disabled = false,
  enableSelectAll = false,
}: UniversalMultiComboboxProps) {
  const effectiveListUrl = listUrl ?? url;
  const { t } = useTranslation();
  const listboxId = React.useId();
  const [open, setOpen] = React.useState(false);
  const [inputValue, setInputValue] = React.useState('');
  const [selectedItems, setSelectedItems] = React.useState<AnyRecord[]>([]);
  const debouncedInput = useDebounce(inputValue, 300);

  const [filters, setFilters] = React.useState<FiltersState>({
    limit: 10,
    page: 0,
    s: undefined,
    ...params,
  });

  const paramsString = React.useMemo(() => JSON.stringify(params), [params]);

  React.useEffect(() => {
    const parsedParams = JSON.parse(paramsString || '{}') as Record<string, unknown>;
    setFilters((prev) => ({
      ...prev,
      ...parsedParams,
      page: 0,
      s: debouncedInput.length >= 2 ? debouncedInput : undefined,
    }));
  }, [paramsString, debouncedInput]);

  const qKey = React.useMemo(
    () => [queryKey, effectiveListUrl, filters.limit, filters.page, filters.s, paramsString],
    [queryKey, effectiveListUrl, filters.limit, filters.page, filters.s, paramsString]
  );

  const { data, isFetching, refetch } = useQuery({
    queryKey: qKey,
    queryFn: async () => {
      const offset = filters.page * filters.limit;
      const queryParams: Record<string, unknown> = { ...filters, offset };
      delete queryParams['page'];
      Object.keys(queryParams).forEach((k) => {
        if (queryParams[k] == null || queryParams[k] === '') {
          delete queryParams[k];
        }
      });
      const res = await api.get<ApiResponse | AnyRecord[]>(effectiveListUrl, { params: queryParams });
      return res;
    },
    enabled: open && !disabled,
    staleTime: 5 * 60 * 1000,
  });

  // Normalize results
  const items: AnyRecord[] = React.useMemo(() => {
    if (Array.isArray(data)) return data;
    if (data && typeof data === 'object' && 'results' in data) {
      return (data as ApiResponse).results ?? [];
    }
    return [];
  }, [data]);

  // Fetch selected items by IDs
  React.useEffect(() => {
    if (!selectedIds || selectedIds.length === 0) {
      setSelectedItems([]);
      return;
    }

    const selectedMap = new Map(selectedItems.map((item) => [item[valueKey], item]));

    const missingIds = selectedIds.filter((id) => !selectedMap.has(id));

    if (missingIds.length === 0) {
      // Nothing new to fetch — but the parent may have removed an id (e.g. the
      // chosen lead is dropped from staff). Prune any stale items so the chips
      // and "N selected" count stay in sync with `selectedIds`.
      const pruned = selectedItems.filter((item) =>
        selectedIds.includes(item[valueKey] as number | string)
      );
      if (pruned.length !== selectedItems.length) setSelectedItems(pruned);
      return;
    }

    const fetchPromises = missingIds.map(async (id) => {
      try {
        const res = await api.get<AnyRecord>(`${url}/${String(id)}`);
        return res;
      } catch {
        return null;
      }
    });

    void Promise.all(fetchPromises).then((newlyFetched) => {
      const filtered = newlyFetched.filter(Boolean) as AnyRecord[];
      const uniqueItems = [...selectedItems, ...filtered].reduce((acc, item) => {
        acc.set(item[valueKey], item);
        return acc;
      }, new Map<unknown, AnyRecord>());

      const result = Array.from(uniqueItems.values()).filter((item) =>
        selectedIds.includes(item[valueKey] as number | string)
      );
      setSelectedItems(result);
    });
  }, [selectedIds, url, valueKey]);

  const toggleItem = (item: AnyRecord) => {
    const exists = selectedItems.some((i) => i[valueKey] === item[valueKey]);
    const updated = exists
      ? selectedItems.filter((i) => i[valueKey] !== item[valueKey])
      : [...selectedItems, item];

    setSelectedItems(updated);
    onSelectItem?.(updated);
  };

  const removeItem = (id: number | string) => {
    const updated = selectedItems.filter((i) => i[valueKey] !== id);
    setSelectedItems(updated);
    onSelectItem?.(updated);
  };

  const [isSelectingAll, setIsSelectingAll] = React.useState(false);

  // Select every record matching `params` (the whole pool), not just the page
  // that's currently loaded. The search box and `hiddenIds` are ignored/excluded.
  const handleSelectAll = async () => {
    setIsSelectingAll(true);
    try {
      const baseParams = JSON.parse(paramsString || '{}') as Record<string, unknown>;
      const queryParams: Record<string, unknown> = { ...baseParams, limit: 1000, offset: 0 };
      Object.keys(queryParams).forEach((k) => {
        if (queryParams[k] == null || queryParams[k] === '') delete queryParams[k];
      });
      const res = await api.get<ApiResponse | AnyRecord[]>(effectiveListUrl, { params: queryParams });
      const all: AnyRecord[] = Array.isArray(res)
        ? res
        : ((res as ApiResponse)?.results ?? []);
      const selectable = all.filter((item) => !hiddenIds.includes(item[valueKey] as number | string));
      setSelectedItems(selectable);
      onSelectItem?.(selectable);
    } finally {
      setIsSelectingAll(false);
    }
  };

  const handleDeselectAll = () => {
    setSelectedItems([]);
    onSelectItem?.([]);
  };

  return (
    <div className={cn('space-y-2', className)}>
      <div className="flex flex-wrap gap-2">
        {selectedItems.map((item) => (
          <span
            key={String(item[valueKey])}
            className="bg-muted text-sm px-2 py-1 rounded flex items-center gap-1"
          >
            {String(item[labelKey])}
            {!disabled && (
              <button type="button" onClick={() => removeItem(item[valueKey] as number | string)}>
                <X className="h-3 w-3" />
              </button>
            )}
          </span>
        ))}
      </div>

      <Popover
        open={!disabled && open}
        onOpenChange={(state) => {
          if (disabled) return;
          setOpen(state);
          if (state) {
            void refetch();
          } else {
            setInputValue('');
            setFilters((prev) => ({ ...prev, s: undefined, page: 0 }));
          }
        }}
      >
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-controls={listboxId}
            aria-expanded={open}
            disabled={disabled}
            className={cn(
              'w-full justify-between',
              disabled && 'pointer-events-none opacity-50'
            )}
          >
            {selectedItems.length > 0 ? `${selectedItems.length} selected` : placeholder}
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>

        {!disabled && (
          <PopoverContent className="w-[220px] p-0">
            <Command shouldFilter={false}>
              <CommandInput
                placeholder={t('search') ?? 'Search...'}
                className="h-9"
                onValueChange={(v) => setInputValue(v ?? '')}
              />
              {enableSelectAll && (
                <div className="flex items-center justify-between gap-2 border-b px-2 py-1.5">
                  <button
                    type="button"
                    onClick={() => void handleSelectAll()}
                    disabled={isSelectingAll}
                    className="text-xs font-medium text-primary hover:underline disabled:opacity-50"
                  >
                    {isSelectingAll ? t('loading') : t('selectAll')}
                  </button>
                  <button
                    type="button"
                    onClick={handleDeselectAll}
                    disabled={isSelectingAll || selectedItems.length === 0}
                    className="text-xs text-muted-foreground hover:underline disabled:opacity-50"
                  >
                    {t('deselectAll')}
                  </button>
                </div>
              )}
              <CommandList id={listboxId}>
                <CommandEmpty>
                  {isFetching
                    ? t('loading')
                    : inputValue.length > 0 && inputValue.length < 2
                      ? t('need2Char') ?? 'Type at least 2 characters'
                      : t('noDataFound') ?? 'No data found.'}
                </CommandEmpty>
                <CommandGroup>
                  {items.map((item) => {
                    const isSelected = selectedItems.some((i) => i[valueKey] === item[valueKey]);
                    const isHidden = hiddenIds.includes(item[valueKey] as number | string);

                    return (
                      <CommandItem
                        key={String(item[valueKey])}
                        value={String(item[valueKey])}
                        className={cn(isHidden && 'hidden')}
                        onSelect={() => toggleItem(item)}
                      >
                        {String(item[labelKey])}
                        <Check
                          className={cn(
                            'ml-auto h-4 w-4',
                            isSelected ? 'opacity-100' : 'opacity-0'
                          )}
                        />
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        )}
      </Popover>
    </div>
  );
}
